import React, { useState } from 'react';
import {
    Box,
    FormControlLabel,
    Switch,
    Button,
    Alert,
    Typography
} from '@mui/material';
import { roomSettingsService } from '../../../client/room/roomSettingsService';
import toast from 'react-hot-toast';
import type { IRoomPrivacy } from '../../../client/room/types';
import { useTranslation } from '@fanfanlo';
import { useProxyWatch } from '@fanfanlo';
import { roomData } from '../../../client/room/data';

export const PrivacySettingsContent = ({ roomId }: { roomId: string }) => {
    const { t } = useTranslation('homepage/im/components/room-settings/privacy/content');
    const [loading, setLoading] = useState(false);
    const [currentSettings] = useProxyWatch(
        roomData.roomSettings.settings,
        [roomId],
        roomData.roomSettings.settings[roomId]
    );
    const [formData, setFormData] = useState<IRoomPrivacy>({
        isPublic: currentSettings?.privacy?.isPublic ?? false,
        isEncrypted: currentSettings?.privacy?.isEncrypted ?? false,
        allowGuestJoin: currentSettings?.privacy?.allowGuestJoin ?? false,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            if (formData.isPublic !== currentSettings?.privacy?.isPublic) {
                await roomSettingsService.updateRoomVisibility(roomId, formData.isPublic);
            }
            if (formData.allowGuestJoin !== currentSettings?.privacy?.allowGuestJoin) {
                await roomSettingsService.updateGuestAccess(roomId, formData.allowGuestJoin);
            }
            if (formData.isEncrypted && !currentSettings?.privacy?.isEncrypted) {
                await roomSettingsService.updateEncryption(roomId, true);
            }
            toast.success(t('PrivacySettingsContent.toast.success'));
        } catch (error: any) {
            toast.error(error.message || t('PrivacySettingsContent.toast.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ '& > *': { mb: 2 } }}>
            <FormControlLabel
                control={
                    <Switch
                        checked={formData.isPublic}
                        onChange={handleChange}
                        name="isPublic"
                        disabled={loading}
                    />
                }
                label={
                    <Box>
                        <Typography>{t('PrivacySettingsContent.form.isPublic.label')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('PrivacySettingsContent.form.isPublic.description')}
                        </Typography>
                    </Box>
                }
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={formData.allowGuestJoin}
                        onChange={handleChange}
                        name="allowGuestJoin"
                        disabled={loading}
                    />
                }
                label={
                    <Box>
                        <Typography>{t('PrivacySettingsContent.form.allowGuestJoin.label')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('PrivacySettingsContent.form.allowGuestJoin.description')}
                        </Typography>
                    </Box>
                }
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={formData.isEncrypted}
                        onChange={handleChange}
                        name="isEncrypted"
                        disabled={loading || currentSettings?.privacy?.isEncrypted}
                    />
                }
                label={
                    <Box>
                        <Typography>{t('PrivacySettingsContent.form.isEncrypted.label')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('PrivacySettingsContent.form.isEncrypted.description')}
                        </Typography>
                    </Box>
                }
            />

            {currentSettings?.privacy?.isEncrypted && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                >
                    {t('PrivacySettingsContent.form.isEncrypted.alert')}
                </Alert>
            )}

            <Button
                type="submit"
                variant="contained"
                disabled={loading}
            >
                {t('PrivacySettingsContent.form.submit')}
            </Button>
        </Box>
    );
}; 